import { useState, useEffect, useMemo } from 'react';
import { Box, Text, Loader, Center, Group, Button, Badge, Menu, ActionIcon, Switch } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconRefresh, IconPlus, IconDots, IconMessageCircle, IconTrash } from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useBot } from '@mygpt/app/bot';
import { useCollaborateStore } from '@mygpt/app/collaborate';
import { getSkillList, toggleSkill, deleteSkill, uploadSkillPackage, type Skill } from '@mygpt/app/apis';
import { I18N_LANG_MAP } from '@mygpt/app';
import { ImportDialog } from './ImportDialog';

const CapabilitiesComponent = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { botId } = useBot();
  const { isAssist } = useCollaborateStore();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const categoryLabels: Record<string, string> = {
    custom: t('capability.category.custom', '自定义技能'),
    other: t('capability.category.other', '其他'),
  };

  const defaultCategoryOrder = ['custom', 'other'];

  const categories = useMemo(() => {
    const groups: Record<string, Skill[]> = {};
    for (const skill of skills) {
      const cat = skill.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(skill);
    }
    const cats = Object.keys(groups);
    cats.sort((a, b) => {
      const ai = defaultCategoryOrder.indexOf(a);
      const bi = defaultCategoryOrder.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return 1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
    return cats.map((cat) => ({
      category: cat,
      label: categoryLabels[cat] || cat,
      count: groups[cat].length,
    }));
  }, [skills]);

  const filteredSkills = useMemo(() => {
    const list = activeCategory === null ? skills : skills.filter((s) => (s.category || 'other') === activeCategory);
    return [...list].sort((a, b) => (a.enabled === b.enabled ? 0 : a.enabled ? -1 : 1));
  }, [skills, activeCategory]);

  // 判断是否是用户上传的Skill（可删除）
  const isUserSkill = (skill: Skill) => {
    return skill.user_skill;
  };

  const loadSkills = async () => {
    if (!botId) {
      return;
    }

    try {
      setLoading(true);
      const response = await getSkillList(botId, I18N_LANG_MAP[i18n.language]);

      setSkills(response.skills);
    } catch (error) {
      showNotification({
        message: t('capability.loadError'),
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImportSkill = () => {
    setImportDialogOpen(true);
  };

  const handleImportConfirm = async (file: File) => {
    if (!botId) return;

    try {
      await uploadSkillPackage(botId, file);
      showNotification({
        message: t('capability.importSuccess'),
        color: 'green',
      });
      await loadSkills();
    } catch (error) {
      throw error;
    }
  };

  const handleTryInChat = (skill: Skill) => {
    if (!botId) return;

    const prefix = isAssist ? '/assist' : '/bot';
    navigate(`${prefix}/${botId}/chat`);
  };

  const handleDelete = (skill: Skill) => {
    // 只有用户上传的技能才能删除
    if (!isUserSkill(skill)) {
      return;
    }

    modals.openConfirmModal({
      title: t('capability.delete.title'),
      children: (
        <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
          {t('capability.delete.content', { name: skill.name })}
        </Text>
      ),
      labels: {
        confirm: t('capability.delete.confirm'),
        cancel: t('capability.delete.cancel'),
      },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        if (!botId) return;

        try {
          await deleteSkill(botId, skill.name);
          showNotification({
            message: t('capability.deleteSuccess'),
            color: 'green',
          });
          await loadSkills();
        } catch (error) {
          showNotification({
            message: t('capability.deleteError'),
            color: 'red',
          });
        }
      },
      centered: true,
    });
  };

  const handleSkillToggle = async (skill: Skill) => {
    if (!botId) return;

    try {
      setActionLoading(skill.name);
      const newEnabled = !skill.enabled;

      const response = await toggleSkill(botId, skill.name, newEnabled);

      // 更新本地状态
      setSkills((prevSkills) =>
        prevSkills.map((s) =>
          s.name === skill.name
            ? {
                ...s,
                enabled: newEnabled,
              }
            : s
        )
      );

      showNotification({
        message: newEnabled ? t('capability.enableSuccess') : t('capability.disableSuccess'),
        color: 'green',
      });
    } catch (error) {
      showNotification({
        message: t('capability.toggleError'),
        color: 'red',
      });
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    if (botId) {
      void loadSkills();
    }
  }, [botId, i18n.language]);

  if (loading) {
    return (
      <Center h={200}>
        <Loader size="md" />
      </Center>
    );
  }

  return (
    <Box>
      <div className="space-y-6">
        {/* Header with action buttons */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Text size="sm" className="text-gray-900 mb-2">
              {t('capability.title')}
            </Text>
            <Text size="sm" className="text-gray-600">
              {t('capability.description')}
            </Text>
          </div>
          <Group gap="sm" wrap="nowrap">
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              onClick={() => loadSkills()}
              loading={loading}
              size="sm"
            >
              {t('common.refresh')}
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={handleImportSkill} size="sm">
              {t('capability.import.button')}
            </Button>
          </Group>
        </div>

        {skills.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
            <Text c="dimmed">{t('capability.noServices')}</Text>
          </div>
        ) : (
          <>
            {categories.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full border transition-colors ${
                    activeCategory === null
                      ? 'bg-indigo-500 border-indigo-500 text-white'
                      : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-indigo-400 hover:text-indigo-500'
                  }`}
                  onClick={() => setActiveCategory(null)}
                >
                  {t('common.all', '全部')}
                  <span className="text-xs opacity-70">{skills.length}</span>
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.category}
                    className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full border transition-colors ${
                      activeCategory === cat.category
                        ? 'bg-indigo-500 border-indigo-500 text-white'
                        : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-indigo-400 hover:text-indigo-500'
                    }`}
                    onClick={() => setActiveCategory(cat.category)}
                  >
                    {cat.label}
                    <span className="text-xs opacity-70">{cat.count}</span>
                  </button>
                ))}
              </div>
            )}
            {filteredSkills.map((skill) => {
              const isUserUploaded = isUserSkill(skill);
              return (
                <div key={skill.name} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <Group gap="sm" mb={4}>
                        <Text size="sm" className="text-gray-900">
                          {skill.name}
                        </Text>
                        {!isUserUploaded && (
                          <Badge size="sm" color="violet" variant="light" tt="none">
                            GBase Support
                          </Badge>
                        )}
                      </Group>
                      <Text size="sm" className="text-gray-600">
                        {skill.description}
                      </Text>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      {/* Toggle Switch */}
                      <Switch
                        checked={skill.enabled}
                        onChange={() => handleSkillToggle(skill)}
                        disabled={actionLoading === skill.name}
                        size="md"
                      />

                      {/* Menu */}
                      <Menu shadow="md" width={160} position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray" size="lg">
                            <IconDots size={20} />
                          </ActionIcon>
                        </Menu.Target>

                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconMessageCircle size={16} />}
                            onClick={() => handleTryInChat(skill)}
                          >
                            {t('capability.actions.tryInChat')}
                          </Menu.Item>
                          {isUserUploaded && (
                            <Menu.Item
                              leftSection={<IconTrash size={16} />}
                              onClick={() => handleDelete(skill)}
                              color="red"
                            >
                              {t('capability.actions.delete')}
                            </Menu.Item>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Import Dialog */}
      <ImportDialog
        opened={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={handleImportConfirm}
      />
    </Box>
  );
};

export default CapabilitiesComponent;
